import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/admindashboard", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!formData.identifier.trim() || !formData.password) {
      setError("Enter your email/mobile number and password.");
      return;
    }

    try {
      setIsSubmitting(true);

      await login({
        identifier: formData.identifier.trim(),
        password: formData.password,
      });

      navigate("/admindashboard", { replace: true });
    } catch (requestError) {
      const message = requestError?.details?.message || requestError.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to sign in. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-white">
      <div className="grid min-h-screen overflow-hidden bg-white lg:grid-cols-2">
        <section className="relative hidden overflow-hidden bg-primary-800 p-12 text-white lg:flex lg:flex-col lg:justify-between">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary-300 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-rose-300 blur-3xl" />
          </div>

          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="h-24 w-24">
                <img src={logo} alt="logo" />
              </div>

              <div>
                <p className="text-lg font-bold tracking-wide">CCPL</p>
                <p className="text-xs font-medium uppercase tracking-[0.25em] text-primary-200">
                  ERP Suite
                </p>
              </div>
            </div>

            <div className="mt-24">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-200">
                Welcome back
              </p>

              <h1 className="mt-4 max-w-md text-4xl font-bold leading-tight">
                One place to run your people, products, and inventory.
              </h1>

              <p className="mt-6 max-w-md text-base leading-7 text-primary-100">
                Secure access for your Charms operations team.
              </p>
            </div>
          </div>

          <div className="relative flex items-center gap-3 text-sm text-primary-100">
            <ShieldCheck size={20} />
            <span>Role-based access and secure sessions</span>
          </div>
        </section>

        <section className="flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div className="w-full max-w-md">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mb-10 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-primary-800"
            >
              <ArrowLeft size={17} />
              Back to store
            </button>

            <div className="lg:hidden">
              <div className="flex items-center gap-3 text-primary-800">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary-800 text-white">
                  <Sparkles size={20} />
                </div>
                <div>
                  <p className="font-bold tracking-wide">CHARMS</p>
                  <p className="text-xs font-medium uppercase tracking-[0.2em]">
                    ERP Suite
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 lg:mt-0">
              <p className="text-sm font-semibold text-primary-800">
                ADMINISTRATION PORTAL
              </p>

              <h2 className="mt-2 text-3xl font-bold text-gray-900">
                Sign in to your account
              </h2>

              <p className="mt-3 text-sm leading-6 text-gray-500">
                Use your official email address or registered mobile number.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-9 space-y-5">
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}
              <div>
                <label
                  htmlFor="identifier"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  Email or mobile number
                </label>

                <div className="relative">
                  <Mail
                    size={19}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    id="identifier"
                    name="identifier"
                    type="text"
                    value={formData.identifier}
                    onChange={handleChange}
                    placeholder="you@charms.com"
                    autoComplete="username"
                    className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-4 text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-600 focus:ring-4 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    Password
                  </label>

                  <button
                    type="button"
                    className="text-sm font-semibold text-primary-800 hover:text-primary-600"
                  >
                    Forgot password?
                  </button>
                </div>

                <div className="relative">
                  <LockKeyhole
                    size={19}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-gray-200 py-3.5 pl-12 pr-12 text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-600 focus:ring-4 focus:ring-primary-100"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-primary-800"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                  </button>
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-600">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-800 focus:ring-primary-700"
                />
                Remember this device
              </label>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="w-full rounded-xl bg-primary-800 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-primary-800/20 transition hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Signing in..." : "Sign in securely"}
              </button>
            </form>

            <p className="mt-8 text-center text-xs leading-5 text-gray-400">
              Authorized Charms employees only. Access is monitored and
              protected.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
